import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FiCheck, FiChevronDown,
  FiGlobe, FiMapPin, FiSliders,
} from "react-icons/fi";
import ContactModal from "../ContactModal/ContactModal";
import Seo, { breadcrumbSchema, faqSchema, organizationSchema, webPageSchema } from "../Seo/Seo";
import heroImage from "../../Assets/products/klean-leaf-laundry-detergent-sheets-packaging-and-sheets.webp";
import ohioImage from "../../Assets/blogs/klean-leaf-columbus-ohio-location-map.webp";
import storyImage from "../../Assets/products/klean-leaf-3-in-1-enzyme-laundry-detergent-sheets-countertop.webp";
import workflowImage from "../../Assets/blogs/private-label-laundry-detergent-sheets-launch-workflow.webp";
import formulaImage from "../../Assets/blogs/laundry-detergent-sheet-formula-testing.webp";
import brandBoxImage from "../../Assets/blogs/your-brand-laundry-detergent-sheets-box.webp";
import facilityImage from "../../Assets/blogs/manufacture.webp";
import productionLineImage from "../../Assets/blogs/blog-hero.webp";
import shippingImage from "../../Assets/shipping-truck.webp";
import {
  LAUNDRY_COLLECTION_PATH, PACKAGING_PATH, aboutFaqs, stats, processSteps,
  differences, customizationOptions, laundryProducts, broaderProducts, packagingOptions,
} from "./aboutContent";

const supportPoints = [
  { icon: FiMapPin, title: "Columbus, Ohio", copy: "Client & sample support" },
  { icon: FiSliders, title: "Private Label", copy: "Formula + packaging customization" },
  { icon: FiGlobe, title: "Our Factory in China", copy: "Manufacturing + quality control" },
];

function SectionHeading({ eyebrow, title, id, children }) {
  return (
    <header className="about-heading">
      {eyebrow && <p className="about-eyebrow">{eyebrow}</p>}
      <h2 id={id}>{title}</h2>
      {children}
    </header>
  );
}

function IconFeatureGrid({ items, className = "" }) {
  return (
    <ul className={`about-feature-grid ${className}`}>
      {items.map(({ icon: Icon, title, copy }) => (
        <li key={title}>
          <Icon aria-hidden="true" />
          <div><h3>{title}</h3><p>{copy}</p></div>
        </li>
      ))}
    </ul>
  );
}

function ProductVisualGrid({ items, className = "" }) {
  return (
    <ul className={`about-product-grid ${className}`}>
      {items.map(({ product, title, to }) => (
        <li key={product.sku}>
          <Link className="about-product" to={to || `/products/${product.slug}`}>
            <div className="about-product-image">
              <img src={product.images[0].src} alt={product.images[0].alt} loading="lazy" decoding="async" width="800" height="600" />
            </div>
            <span>{title}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function PhotoFeature({ src, alt, width, height, eyebrow, title, children, className = "" }) {
  return (
    <figure className={`about-photo-feature ${className}`}>
      <div className="about-photo-feature-image">
        <img src={src} alt={alt} width={width} height={height} loading="lazy" decoding="async" />
      </div>
      <figcaption>
        <p className="about-eyebrow">{eyebrow}</p>
        {title && <h3>{title}</h3>}
        <p>{children}</p>
      </figcaption>
    </figure>
  );
}

function About() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const showContactModal = () => setIsContactModalOpen(true);

  return (
    <main className="about-page" id="about">
      <Seo
        title="About Our Laundry Sheet Manufacturing | Klean Leaf"
        description="Klean Leaf specializes in private label laundry detergent sheet manufacturing, custom formulas, packaging, and brand development, plus other home-care formats."
        path="/about"
        schema={[
          organizationSchema,
          webPageSchema({
            name: "About Our Laundry Sheet Manufacturing | Klean Leaf",
            description: "Klean Leaf specializes in private label laundry detergent sheet manufacturing, custom formulas, packaging, and brand development, plus other home-care formats.",
            path: "/about",
            type: "AboutPage",
            primaryImage: heroImage,
          }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]),
          faqSchema(aboutFaqs),
        ]}
      />

      <section className="about-hero" aria-labelledby="about-title">
        <div className="about-container about-hero-grid">
          <div className="about-hero-copy">
            <p className="about-eyebrow"><span className="about-small-line" />About Klean Leaf</p>
            <h1 id="about-title">Private Label Laundry Detergent Sheets <span>Manufacturer</span></h1>
            <p className="about-lead">Helping brands develop, customize, package and manufacture retail-ready laundry detergent sheets.</p>
            <div className="about-actions">
              <Link className="about-button" to={LAUNDRY_COLLECTION_PATH}>Explore Laundry Detergent Sheets</Link>
              <button className="about-button about-button-outline" onClick={showContactModal} type="button">Start Your Project</button>
            </div>
            <p className="about-hero-note">Your product. Your packaging. Your brand.</p>
          </div>
          <figure className="about-hero-visual">
            <img src={heroImage} alt="Klean Leaf laundry detergent sheets beside custom retail packaging" width="1600" height="1200" decoding="async" />
            <figcaption><span>Small sheets. Possibilities for your brand.</span><span>01 / Laundry care</span></figcaption>
          </figure>
        </div>
      </section>

      <section className="about-section about-introduction" aria-labelledby="about-who-title">
        <div className="about-container">
          <div className="about-split">
            <figure className="about-support-map">
              <img src={ohioImage} alt="Map of the United States highlighting Klean Leaf’s client support location in Columbus, Ohio" width="1536" height="1024" loading="lazy" decoding="async" />
              <figcaption><FiMapPin aria-hidden="true" /><span>Your point of contact in Columbus.<small>Product discussions, samples and project updates in Eastern Time.</small></span></figcaption>
            </figure>
            <div className="about-split-copy">
              <SectionHeading eyebrow="Who we are" title="Laundry Sheet Manufacturing, Made Easier." id="about-who-title" />
              <p>Klean Leaf combines U.S.-based project support with production at our own factory in China to help brands launch private label laundry detergent sheets from sample to finished retail packaging. For brands, retailers, wholesalers and refill shops, that means a compact product format shaped around your customers and sales channels.</p>
              <IconFeatureGrid items={supportPoints} className="about-support-points" />
              <a className="about-button about-button-outline" href="/docs/klean-leaf-company-profile.pdf" target="_blank" rel="noopener noreferrer">Download Our Company Profile</a>
            </div>
          </div>
          <div className="about-stats" aria-label="Klean Leaf at a glance">
            {stats.map(({ value, label }) => (
              <div className="about-stat" key={label}><strong>{value}</strong><span>{label}</span></div>
            ))}
          </div>
          <p className="about-fine-print">*Starting MOQ: 1,000 retail package units per SKU; varies with formula and packaging. Typical first-order production: 45–60 days after approvals, depending on specifications and capacity. Shipping time is additional.</p>
        </div>
      </section>

      <section className="about-section about-story" aria-labelledby="about-story-title">
        <div className="about-container about-split about-story-grid">
          <figure className="about-photo about-story-photo">
            <img src={storyImage} alt="Klean Leaf enzyme laundry detergent sheets and retail packaging in a laundry room" width="1600" height="1200" loading="lazy" decoding="async" />
          </figure>
          <div className="about-split-copy">
            <SectionHeading eyebrow="Our story" title="Built Around Better Private Label Support" id="about-story-title" />
            <p>Entering the laundry sheet market should not mean navigating overseas manufacturing on your own. Klean Leaf was built to give brands a clearer path from an early idea to a product ready for the shelf.</p>
            <p>We bring formula, packaging, production and logistics into one coordinated project, so you can focus on building your brand. Lightweight sheets are easy to store and ship, with formats suited to retail shelves, e-commerce, refill programs and subscriptions.</p>
            <p className="about-pull-quote">From first sample to retail-ready product.</p>
          </div>
        </div>
      </section>

      <section className="about-section about-process" aria-labelledby="about-process-title">
        <div className="about-container">
          <SectionHeading eyebrow="How we work" title="From Idea to Retail-Ready Laundry Sheets" id="about-process-title">
            <p>A clear path, with the right support at each step.</p>
          </SectionHeading>
          <ol className="about-process-flow">
            {processSteps.map(({ icon: Icon, title, copy }, index) => (
              <li key={title}>
                <div className="about-process-marker"><span>{String(index + 1).padStart(2, "0")}</span><Icon aria-hidden="true" /></div>
                <h3>{title}</h3><p>{copy}</p>
              </li>
            ))}
          </ol>
          <div className="about-launch-layout">
            <a className="about-workflow-image" href={workflowImage} target="_blank" rel="noopener noreferrer" aria-label="View Full Workflow image in a new tab">
              <img src={workflowImage} alt="First-order workflow: inquiry, meeting, samples, packaging selection, dieline, artwork, packaging sample review and final confirmation before placing an order" width="1501" height="1048" loading="lazy" decoding="async" />
              <span className="about-button about-button-outline">View Full Workflow</span>
            </a>
            <div className="about-launch-copy">
              <p className="about-eyebrow">Your first order</p>
              <h3>A Clear Start.<br />A Confident First Order.</h3>
              <p>Review the product in your hands, choose your packaging and confirm the details before production begins.</p>
              <ul className="about-launch-checkpoints">
                <li><span>01</span><div><h4>Start with samples</h4><p>Compare formula, fragrance and sheet feel.</p></div></li>
                <li><span>02</span><div><h4>Shape your packaging</h4><p>Select a format and prepare artwork using our dielines.</p></div></li>
                <li><span>03</span><div><h4>Approve the details</h4><p>Review the paper packaging sample and final specification.</p></div></li>
              </ul>
              <button className="about-button about-button-outline" type="button" onClick={showContactModal}>Request Your First Samples</button>
            </div>
          </div>
        </div>
      </section>

      <section className="about-section about-difference" aria-labelledby="about-difference-title">
        <div className="about-container">
          <div className="about-difference-layout">
            <SectionHeading eyebrow="The Klean Leaf approach" title="The Details Behind Your Brand" id="about-difference-title">
              <p>From the way a sheet dissolves to the way a box looks on the shelf, the details make the product yours.</p>
              <button className="about-button about-button-outline" type="button" onClick={showContactModal}>Meet Your Manufacturing Partner</button>
            </SectionHeading>
            <IconFeatureGrid items={differences} className="about-difference-grid" />
          </div>
          <div className="about-development-gallery">
            <PhotoFeature src={formulaImage} alt="Klean Leaf laboratory team reviewing detergent sheet formulas and samples" width="1200" height="795" eyebrow="Formula development" title="Refine the Formula">
              Compare sheet feel, dissolution, fragrance and cleaning performance. Sample feedback helps guide the product before you confirm production.
            </PhotoFeature>
            <PhotoFeature src={brandBoxImage} alt="Custom laundry detergent sheet box with Your Brand artwork and botanical detailing" width="1433" height="1075" eyebrow="Packaging & artwork" title="Make the Packaging Yours" className="about-brand-box">
              Turn your chosen format into a retail-ready pack, with dielines for your designer and a paper packaging sample to review.
            </PhotoFeature>
          </div>
        </div>
      </section>

      <section className="about-section about-products" aria-labelledby="about-products-title">
        <div className="about-container">
          <div className="about-section-topline">
            <SectionHeading eyebrow="Our core expertise" title="Built Around Your Brand" id="about-products-title">
              <p>Start with the laundry sheet format that fits your customers.</p>
            </SectionHeading>
            <Link className="about-button about-button-outline" to={LAUNDRY_COLLECTION_PATH}>Explore Laundry Sheet Products</Link>
          </div>
          <ProductVisualGrid items={laundryProducts} />
          <div className="about-customization">
            <p>Make it yours</p>
            <ul>{customizationOptions.map(({ icon: Icon, title }) => <li key={title}><Icon aria-hidden="true" /><span>{title}</span></li>)}</ul>
          </div>
        </div>
      </section>

      <section className="about-section about-packaging" aria-labelledby="about-packaging-title">
        <div className="about-container">
          <div className="about-section-topline">
            <SectionHeading eyebrow="The finishing touch" title="Packaging That Fits Your Market" id="about-packaging-title" />
            <Link className="about-button about-button-outline" to={PACKAGING_PATH}>See Packaging &amp; Product Options</Link>
          </div>
          <ul className="about-packaging-grid">
            {packagingOptions.map((option) => (
              <li key={option.id}><img src={option.items[0].image} alt={`${option.name} packaging option for laundry detergent sheets`} width="280" height="280" loading="lazy" decoding="async" /><h3>{option.name}</h3></li>
            ))}
          </ul>
          <p className="about-fine-print">Packaging availability varies by product and order quantity.</p>
        </div>
      </section>

      <section className="about-section about-production" aria-labelledby="about-production-title">
        <div className="about-container">
          <div className="about-production-header">
            <SectionHeading eyebrow="From approval to delivery" title="Connected From Production to Shipment" id="about-production-title" />
            <div className="about-production-copy">
              <ul className="about-check-list">
                <li><FiCheck aria-hidden="true" />Formula &amp; sample confirmation</li>
                <li><FiCheck aria-hidden="true" />Production &amp; packaging coordination</li>
                <li><FiCheck aria-hidden="true" />Inspection &amp; logistics support</li>
              </ul>
              <Link className="about-button about-button-outline" to="/production">See How We Produce</Link>
            </div>
          </div>
          <div className="about-production-gallery">
            <PhotoFeature src={productionLineImage} alt="Laundry detergent sheet manufacturing line with production equipment, sheet rolls and factory team" width="1280" height="1247" eyebrow="Detergent sheet manufacturing · China">
              Production guided by your approved formula and laundry sheet specifications.
            </PhotoFeature>
            <PhotoFeature src={facilityImage} alt="Industrial manufacturing facility with storage tanks and production buildings" width="1343" height="1171" eyebrow="Our factory in China">
              At our own factory in China, we handle production, packing and quality control according to your approved product specifications.
            </PhotoFeature>
            <PhotoFeature src={shippingImage} alt="Pallets of Klean Leaf cartons being loaded into a shipping container with a forklift" width="1618" height="972" eyebrow="Packing & logistics">
              Carton planning, inspection support and shipping coordination connect your finished order with its next destination.
            </PhotoFeature>
          </div>
        </div>
      </section>

      <section className="about-section about-broader" aria-labelledby="about-broader-title">
        <div className="about-container">
          <div className="about-section-topline">
            <SectionHeading eyebrow="More ways to grow" title="Beyond Laundry Sheets" id="about-broader-title">
              <p>Expand your product line with complementary cleaning formats.</p>
            </SectionHeading>
            <Link className="about-button about-button-outline" to="/products">Explore All Products</Link>
          </div>
          <ProductVisualGrid items={broaderProducts} className="about-broader-grid" />
        </div>
      </section>

      <section className="about-section about-faq" aria-labelledby="about-faq-title">
        <div className="about-container about-faq-layout">
          <SectionHeading eyebrow="A few useful answers" title="Before We Get Started" id="about-faq-title">
            <p>Questions about your private label project? Start here.</p>
            <button className="about-button about-button-outline" type="button" onClick={showContactModal}>Talk to Our Team</button>
          </SectionHeading>
          <div className="about-accordion">
            {aboutFaqs.map(({ question, answer }, index) => {
              const isOpen = openFaq === question;
              const triggerId = `about-faq-trigger-${index}`;
              const panelId = `about-faq-panel-${index}`;

              return (
                <div className="about-accordion-item" key={question}>
                  <h3 className="about-accordion-heading">
                    <button
                      className="about-accordion-trigger"
                      type="button"
                      id={triggerId}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setOpenFaq(current => current === question ? null : question)}
                    >
                      {question}<FiChevronDown aria-hidden="true" />
                    </button>
                  </h3>
                  <div
                    className="about-accordion-panel"
                    id={panelId}
                    role="region"
                    aria-labelledby={triggerId}
                    aria-hidden={!isOpen}
                  >
                    <div className="about-accordion-content"><p>{answer}</p></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="about-final-cta" aria-labelledby="about-cta-title">
        <div className="about-container about-cta-layout">
          <div><p className="about-eyebrow">Let’s make it yours</p><h2 id="about-cta-title">Ready to Build Your Laundry Sheet Brand?</h2><p>Explore products, compare options, or talk with our team about your project.</p></div>
          <div className="about-actions">
            <button className="about-button about-button-light" type="button" onClick={showContactModal}>Request Samples or Quote</button>
            <Link className="about-button about-button-outline" to={LAUNDRY_COLLECTION_PATH}>Explore Laundry Sheets</Link>
          </div>
        </div>
      </section>
      <ContactModal mobileTypography isModalOpen={isContactModalOpen} handleCancel={() => setIsContactModalOpen(false)} idPrefix="about-page-contact" />
    </main>
  );
}

export default About;
